import { memo, useMemo, useEffect } from 'react';
import {
  Handle,
  Position,
  useStore,
  useUpdateNodeInternals,
  useReactFlow,
} from '@xyflow/react';
import { buildHandleId } from '../../lib/utils';
import { HANDLES_TYPES, NODES_TYPES, QUEST_TYPES } from '../../types';
import { useGetHandleClasses } from '../hooks/useGetHandleClasses';
import CustomNode from './CustomNode';
import HandlesWrapper from '../HandlesWrapper';

type QuestNodeProps = {
  id: string;
  data: {
    name: string;
    questType: QUEST_TYPES;
  };
  type: NODES_TYPES;
  selected: boolean;
};

const topStyles = (leftOffset?: number, rightOffset?: number) => ({
  position: 'absolute',
  left: leftOffset || undefined,
  right: rightOffset || undefined,
  top: 0,
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'row-reverse',
});

const QuestNode = ({ id, data, type, selected }: QuestNodeProps) => {
  const updateNodeInternals = useUpdateNodeInternals();
  const { getHandleClasses } = useGetHandleClasses();
  const { getNodes } = useReactFlow();
  const edges = useStore((state) => state.edges);
  const sourceEdges = edges.filter((edge) => edge.source === id);

  const linkInputId = buildHandleId({
    type: 'input',
    handleType: HANDLES_TYPES.EVENT_LINK,
    nodeId: id,
  });

  const linkOutputId = buildHandleId({
    type: 'output',
    handleType: `${HANDLES_TYPES.EVENT}-link`,
    nodeId: id,
  });

  const targets = useMemo(() => {
    return sourceEdges
      .filter((edge) => {
        const target = getNodes().find((el) => el.id === edge.target);
        const source = getNodes().find((el) => el.id === edge.source);

        if (edge.sourceHandle === linkOutputId) {
          return false;
        }

        if (
          target?.type === NODES_TYPES.EVENT_LINK ||
          source?.type === NODES_TYPES.EVENT_LINK
        ) {
          return false;
        }

        return true;
      })
      .map((edge) => {
        const target = getNodes().find((el) => el.id === edge.target);
        const label =
          target?.type === NODES_TYPES.ACTION
            ? 'Action'
            : target?.type === NODES_TYPES.COLLECTIBLE
            ? 'Collectible'
            : target?.type === NODES_TYPES.DESTROYABLE
            ? 'Destroyable'
            : 'Unknown';
        return {
          id: edge.sourceHandle!,
          label,
        };
      });
  }, [sourceEdges, id]);

  const staticOutputs = useMemo(
    () => [
      {
        label: 'New Edge*',
        id: buildHandleId({
          type: 'output',
          handleType: HANDLES_TYPES.QUEST,
          nodeId: id,
        }),
      },
    ],
    [id]
  );

  useEffect(() => {
    updateNodeInternals(id);
  }, [targets.length]);

  const questTypeLabel =
    data.questType === QUEST_TYPES.SEQUENTIAL ? 'Seq' : 'Par';

  return (
    <CustomNode
      id={id}
      data={data}
      label={`Quest: ${data.name} (${questTypeLabel})`}
      type={type}
      selected={selected}
    >
      <div style={topStyles(20) as React.CSSProperties}>
        <Handle
          type='target'
          position={Position.Top}
          id={linkInputId}
          isConnectable={true}
          className={getHandleClasses(linkInputId, 'target')}
        />
        <span style={{ marginRight: 8, marginTop: 5, fontSize: 12 }}>
          Link Input
        </span>
      </div>
      <div style={topStyles(0, 20) as React.CSSProperties}>
        <Handle
          type='source'
          position={Position.Top}
          id={linkOutputId}
          isConnectable={true}
          className={getHandleClasses(linkOutputId, 'source')}
        />
        <span style={{ marginRight: 8, marginTop: 5, fontSize: 12 }}>
          Link Output
        </span>
      </div>
      <HandlesWrapper targets={targets} newOutputs={staticOutputs} />
    </CustomNode>
  );
};

export default memo(QuestNode);
